import React, { useState } from 'react';
import { Platform, Pressable, StyleSheet, Switch, Text, View } from 'react-native';
import DateTimePicker from '@react-native-community/datetimepicker';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { GlassCard } from '@/components/shared/redesign/GlassCard';
import {
  REDESIGN_SPACING,
  REDESIGN_TYPOGRAPHY,
} from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

interface Props {
  isAnonymous: boolean;
  onIsAnonymousChange: (v: boolean) => void;
  allowReplies: boolean;
  onAllowRepliesChange: (v: boolean) => void;
  expiresAt: Date | null;
  onExpiresAtChange: (v: Date | null) => void;
}

export const SurveySettingsPanel: React.FC<Props> = ({
  isAnonymous,
  onIsAnonymousChange,
  allowReplies,
  onAllowRepliesChange,
  expiresAt,
  onExpiresAtChange,
}) => {
  const { colors } = useThemeColors();
  const [showPicker, setShowPicker] = useState(false);

  return (
    <GlassCard
      variant="soft"
      borderRadius={REDESIGN_SPACING.cardBorderRadius}
      padding={REDESIGN_SPACING.cardPadding}
    >
      <ToggleRow
        label="Anonymous responses"
        helper="Responses won't be linked to a resident."
        value={isAnonymous}
        onChange={onIsAnonymousChange}
      />
      <Divider />
      <ToggleRow
        label="Allow discussion replies"
        helper="Residents can reply on the thread."
        value={allowReplies}
        onChange={onAllowRepliesChange}
      />
      <Divider />

      <Pressable
        onPress={() => setShowPicker(true)}
        style={styles.row}
        accessibilityRole="button"
        accessibilityLabel="Set survey expiration"
      >
        <View style={styles.rowText}>
          <Text style={[REDESIGN_TYPOGRAPHY.body, { color: colors.text.primary }]}>
            Expiration
          </Text>
          <Text style={[REDESIGN_TYPOGRAPHY.small, { color: colors.text.secondary }]}>
            {expiresAt ? expiresAt.toLocaleString() : 'No expiration'}
          </Text>
        </View>
        <View style={styles.rowAction}>
          {expiresAt && (
            <Pressable
              onPress={() => onExpiresAtChange(null)}
              hitSlop={8}
              accessibilityLabel="Clear expiration"
            >
              <Ionicons name="close-circle" size={20} color={colors.text.tertiary} />
            </Pressable>
          )}
          <Ionicons name="time-outline" size={20} color={colors.newsfeed.tagActiveText} />
        </View>
      </Pressable>

      {showPicker && (
        <DateTimePicker
          value={expiresAt ?? new Date(Date.now() + 7 * 24 * 3600 * 1000)}
          mode="datetime"
          minimumDate={new Date()}
          onChange={(_e, date) => {
            if (Platform.OS === 'android') setShowPicker(false);
            if (date) onExpiresAtChange(date);
          }}
        />
      )}
    </GlassCard>
  );
};

const ToggleRow: React.FC<{
  label: string;
  helper?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, helper, value, onChange }) => {
  const { colors, components } = useThemeColors();
  return (
    <View style={styles.row}>
      <View style={styles.rowText}>
        <Text style={[REDESIGN_TYPOGRAPHY.body, { color: colors.text.primary }]}>{label}</Text>
        {helper && (
          <Text style={[REDESIGN_TYPOGRAPHY.small, { color: colors.text.secondary }]}>
            {helper}
          </Text>
        )}
      </View>
      <Switch
        value={value}
        onValueChange={onChange}
        trackColor={{ false: components.toggle.trackOff, true: components.toggle.trackOn }}
        thumbColor={components.toggle.thumb}
      />
    </View>
  );
};

const Divider: React.FC = () => {
  const { colors } = useThemeColors();
  return <View style={[styles.divider, { backgroundColor: colors.card.border }]} />;
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: REDESIGN_SPACING.sectionGap,
    gap: 12,
  },
  rowText: { flex: 1, gap: 2 },
  rowAction: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  divider: { height: StyleSheet.hairlineWidth },
});
